import React from 'react';
import { Award, Globe, Shield, CheckCircle, Phone } from 'lucide-react';

const SANZPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/pdur2m9f_SANZ%20COVER%20PHOTO.png"
          alt="SANZ Standards"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              <span className="text-yellow-400">SANZ</span> Standards
            </h1>
            <p className="text-xl text-gray-300">Australian & New Zealand Bungee Jumping Standards</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Globe className="text-yellow-400 flex-shrink-0 mt-1" size={40} />
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">SANZ on Bungee Jumping</h2>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  India currently does not have its own official standards for bungee jumping, so at Indya Bungy, we strictly follow the <strong className="text-white">SANZ (Standards of Australia and New Zealand)</strong> to ensure world-class safety and professionalism in every jump.
                </p>
              </div>
            </section>

            {/* Australian Standards */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Australian Adventure Activity Standards (AAAS)</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  In Australia, the <strong className="text-white">Australian Adventure Activity Standards (AAAS)</strong> provide detailed guidelines for bungee jumping and other adventure sports, covering:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start">
                    <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span>Equipment quality and certification</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span>Instructor training and qualifications</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span>Risk management procedures</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span>Regular equipment inspection and maintenance</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* New Zealand Standards */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">New Zealand Adventure Activities Regulations</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  In New Zealand, the <strong className="text-white">Adventure Activities Regulations 2016</strong> define safety requirements for bungee jumping operators, ensuring that all equipment is regularly inspected, tested, and maintained for safe bungee jumping experiences.
                </p>
                <p>
                  Both Australia and New Zealand also have strong industry associations like the Australian Adventure Tourism Industry Association (AATIA) and the Adventure Tourism Association (ATA), which promote safety and best practices across extreme adventure sports.
                </p>
              </div>
            </section>

            {/* Image Section */}
            <section className="rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&q=80"
                alt="SANZ Compliance"
                className="w-full h-64 object-cover"
              />
            </section>

            {/* Indya Bungy Implementation */}
            <section className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Our Commitment to SANZ Standards</h3>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  At Indya Bungy, we proudly implement these same international standards to provide India's best bungee jumping experience. Our team ensures every jump follows global safety protocols, using certified equipment and expert jump masters.
                </p>
                <p>
                  Our Goa location, Goa Bungy, is a reputed and registered bungee jumping operator in Goa, approved by the Ministry of Tourism (MOT), Government of Goa, and Goa Tourism, and is also an allied member of ATOAI (Adventure Tour Operators Association of India).
                </p>
                <p>
                  We are committed to offering adventure seekers the safest and most thrilling bungee jumping in India — from Goa, Delhi, and Coorg to our upcoming sites. Whether you're looking for the highest bungee jump in India, the best bungee jumping price, or an unforgettable extreme adventure sports experience, Indya Bungy guarantees safety, thrill, and the ultimate leap of faith.
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center">
              <a
                href="tel:8506865393"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Us: 8506865393</span>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SANZPage;
